import React, { useEffect, useMemo, useState } from 'react';
import { Card, Typography, Space, Tag, Button, Tooltip } from 'antd';
import { normalizeBase } from '@digiwedge/ui-health-panels';

type Json = Record<string, unknown>;

export interface RegistrySystemDetailsPanelProps {
  base: string;
}

export default function RegistrySystemDetailsPanel({ base }: RegistrySystemDetailsPanelProps) {
  const { Text } = Typography;
  const readyUrl = useMemo(() => (base ? `${normalizeBase(base)}/health/ready` : ''), [base]);
  const [data, setData] = useState<Json | null>(null);
  const [status, setStatus] = useState<'idle' | 'ok' | 'fail'>('idle');
  const [latency, setLatency] = useState<number | null>(null);

  useEffect(() => {
    let alive = true;
    async function run() {
      if (!readyUrl) return;
      const start = Date.now();
      try {
        const res = await fetch(readyUrl, { credentials: 'include' });
        const ms = Date.now() - start;
        if (!alive) return;
        setLatency(ms);
        const text = await res.text();
        const body = text ? (JSON.parse(text) as Json) : {};
        setData(body);
        setStatus(res.ok ? 'ok' : 'fail');
      } catch {
        if (!alive) return;
        setStatus('fail');
        setData(null);
        setLatency(null);
      }
    }
    void run();
    return () => {
      alive = false;
    };
  }, [readyUrl]);

  const openJson = () => {
    if (readyUrl) window.open(readyUrl, '_blank', 'noopener,noreferrer');
  };
  const copyJson = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(data ?? {}, null, 2));
    } catch {
      /* noop */
    }
  };

  const statusColor = status === 'ok' ? 'green' : status === 'fail' ? 'red' : 'default';
  const version = typeof data?.version === 'string' ? (data?.version as string) : undefined;
  const dbStatus = (data?.db as Json | undefined)?.status;
  const redisStatus = (data?.redis as Json | undefined)?.status;

  return (
    <Card
      title="Registry System Details"
      extra={<Tag color={statusColor}>{status.toUpperCase()}</Tag>}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <Space size="small" wrap>
          <Text strong>Endpoint</Text>
          <Text code>{readyUrl || 'not set'}</Text>
          {readyUrl && (
            <>
              <Button size="small" onClick={openJson} aria-label="Open JSON">
                Open
              </Button>
              <Button size="small" onClick={copyJson} aria-label="Copy JSON">
                Copy JSON
              </Button>
            </>
          )}
        </Space>
        <Space size="small" wrap>
          <Tag color={statusColor}>status: {status}</Tag>
          {typeof dbStatus === 'string' && (
            <Tag color={dbStatus === 'ok' ? 'green' : 'red'}>db: {String(dbStatus)}</Tag>
          )}
          {typeof redisStatus === 'string' && (
            <Tag color={redisStatus === 'ok' ? 'green' : 'red'}>redis: {String(redisStatus)}</Tag>
          )}
          {typeof version === 'string' && <Tag color="blue">version: {version}</Tag>}
          {typeof latency === 'number' && (
            <Tooltip title="Measured request latency">
              <Tag>{latency} ms</Tag>
            </Tooltip>
          )}
        </Space>
      </Space>
    </Card>
  );
}
